import { useTranslation } from '@kgc/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@kgc/ui';
import { Activity, BarChart3, Calendar } from 'lucide-react';
import type { PublishedWorkflow, WorkflowExecution } from './types';

interface FlowOperationsTabProps {
  workflows: PublishedWorkflow[];
  executions: WorkflowExecution[];
  isOnline: boolean;
}

export function FlowOperationsTab({ workflows, executions, isOnline }: FlowOperationsTabProps) {
  const { t } = useTranslation();

  const todayExecutions = executions.filter(e => {
    const date = new Date(e.startedAt);
    return date.toDateString() === new Date().toDateString();
  });

  const completedToday = todayExecutions.filter(e => e.status === 'completed').length;
  const failedToday = todayExecutions.filter(e => e.status === 'failed').length;
  const cancelledToday = todayExecutions.filter(e => e.status === 'cancelled').length;
  const successRate =
    todayExecutions.length > 0 ? Math.round((completedToday / todayExecutions.length) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="flow-operations-panel">
      {/* Engine Health */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="h-4 w-4" aria-hidden="true" />
            {t('integration.flow.operations.engineHealth')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <div className="flex items-center gap-2">
            <span
              className={
                isOnline ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'
              }
            >
              ●
            </span>
            <span>
              {isOnline
                ? t('integration.flow.operations.online')
                : t('integration.flow.operations.offline')}
            </span>
          </div>
          <p className="text-muted-foreground">
            {workflows.length} {t('integration.flow.operations.registeredWorkflows')}
          </p>
        </CardContent>
      </Card>

      {/* Daily Stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4" aria-hidden="true" />
            {t('integration.flow.operations.dailyStats')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold text-foreground">{todayExecutions.length}</p>
              <p className="text-xs text-muted-foreground">
                {t('integration.flow.operations.totalToday')}
              </p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-500 dark:text-green-400">
                {successRate}%
              </p>
              <p className="text-xs text-muted-foreground">
                {t('integration.flow.operations.successRate')}
              </p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500 dark:text-red-400">{failedToday}</p>
              <p className="text-xs text-muted-foreground">{t('integration.flow.status.failed')}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-muted-foreground">{cancelledToday}</p>
              <p className="text-xs text-muted-foreground">
                {t('integration.flow.status.cancelled')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedules placeholder */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4" aria-hidden="true" />
            {t('integration.flow.operations.schedules')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t('integration.flow.operations.schedulesInfo')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
